import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './GearPage.css';
import SEO from '../../Components/SEO/SEO';
import { getAllCertifications } from '../../api/certification';
import { getFeaturedShorts } from '../../api/shortsVideo';
import { getToken } from '../../api/index';
import Footer from '../../Components/Donation/Footer/Footer';

const CATEGORY_PALETTE = {
  'Clean Water': { icon: '💧', color: '#2E6FD8' },
  'Public Transit': { icon: '🚌', color: '#5AAF9A' },
  'Food Supply': { icon: '🌾', color: '#2DAA6E' },
  'Human Protection': { icon: '🛡️', color: '#F5A623' },
  'Environment': { icon: '🌲', color: '#4CAF50' },
  'Healthcare': { icon: '🏥', color: '#E91E63' },
  'Civic Infrastructure': { icon: '🏛️', color: '#00BCD4' },
  'Education': { icon: '🎓', color: '#9C27B0' },
  'Technology': { icon: '⚡', color: '#FF9800' }
};

const getCategoryMeta = (categoryName) => {
  if (!categoryName) return { name: 'Civic Truth', icon: '📱', color: '#5AAF9A' };
  const match = Object.keys(CATEGORY_PALETTE).find(
    k => k.toLowerCase() === categoryName.toLowerCase()
  );
  if (match) return { name: match, ...CATEGORY_PALETTE[match] };
  return { name: categoryName, icon: '🌐', color: '#5AAF9A' };
};

const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:45';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const formatCount = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toString();
};

const getInitials = (name) => {
  if (!name) return 'AM';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const mapShortToTruth = (video, index) => {
  const categoryName = video.voteResponse?.categories?.[0]?.name || (video.voteId ? 'Ballot Short' : 'Community');
  const meta = getCategoryMeta(categoryName);
  const ballotLabel = video.voteResponse?.title
    ? video.voteResponse.title
    : (video.voteId ? `BL-${video.voteId.toString().slice(0, 8).toUpperCase()}` : null);

  const authorHandle = video.createdByUserName
    ? (video.createdByUserName.startsWith('@')
      ? video.createdByUserName
      : `@${video.createdByUserName.toLowerCase().replace(/\s+/g, '_')}`)
    : '@citizen';

  return {
    id: video.videoId || `api-short-${index}`,
    title: video.title && video.title.trim() ? video.title.trim() : null,
    author: authorHandle,
    avatar: getInitials(video.createdByUserName),
    avatarUrl: video.createdByUserPhoto || null,
    city: null,
    duration: video.duration ? formatDuration(video.duration) : null,
    likes: video.likeCount || 0,
    comments: video.commentCount || 0,
    category: meta.name,
    categoryIcon: meta.icon,
    badgeColor: meta.color,
    previewDesc: video.description && video.description.trim() ? video.description.trim() : null,
    linkedBallot: ballotLabel,
    voteId: video.voteId,
    videoUrl: video.videoUrl,
    thumbnailUrl: video.thumbnailUrl || null
  };
};

/* ── Data: Post Truths (Short Videos) ──────────────────────────── */
const TRUTHS_DATA = [
  {
    id: 1,
    title: 'District 4 Water Pipeline Inspection Reality',
    author: '@marcus_vance',
    avatar: 'MV',
    city: 'Vancouver, BC',
    duration: '0:42',
    views: '14.2k',
    validations: '1,280 verified',
    category: 'Clean Water',
    categoryIcon: '💧',
    badgeColor: '#2E6FD8',
    previewDesc: 'Unfiltered ground footage of pipeline pressure loss affecting 12 residential blocks.',
    linkedBallot: 'BL-2026-041'
  },
  {
    id: 2,
    title: 'Night Shift Transit Access for Hospital Staff',
    author: '@nurse_sarah',
    avatar: 'NS',
    city: 'Austin, TX',
    duration: '0:54',
    views: '28.9k',
    validations: '3,410 verified',
    category: 'Public Transit',
    categoryIcon: '🚌',
    badgeColor: '#5AAF9A',
    previewDesc: 'Healthcare workers stranded at 11:30 PM due to route cancellations. Urgent need for ballot sequence.',
    linkedBallot: 'BL-2026-056'
  },
  {
    id: 3,
    title: 'Zero-Waste Community Kitchen Daily Distribution',
    author: '@elena_kitchen',
    avatar: 'EK',
    city: 'London, UK',
    duration: '0:38',
    views: '19.4k',
    validations: '2,105 verified',
    category: 'Food Supply',
    categoryIcon: '🌾',
    badgeColor: '#2DAA6E',
    previewDesc: 'Surplus organic produce rescued from wholesalers and served to 450 neighborhood families.',
    linkedBallot: 'BL-2026-112'
  },
  {
    id: 4,
    title: 'Emergency Cold-Weather Transition Shelters',
    author: '@community_patrol',
    avatar: 'CP',
    city: 'Denver, CO',
    duration: '0:48',
    views: '32.1k',
    validations: '4,890 verified',
    category: 'Human Protection',
    categoryIcon: '🛡️',
    badgeColor: '#F5A623',
    previewDesc: 'Volunteer team documenting warmth pod deployment during sudden sub-zero weather emergency.',
    linkedBallot: 'BL-2026-073'
  }
];

/* ── Data: Problem Catalogue & Ballot Sequences ────────────────── */
const CATALOGUE_DATA = [
  {
    id: 'PRB-2026-041',
    ballotId: 'BL-2026-041',
    dept: 'Healthcare',
    deptIcon: '🏥',
    title: 'Community Mobile Pediatric Clinic & Supplies',
    city: 'Vancouver, Canada',
    stage: 5,
    stageLabel: 'Facilitated & Disbursed',
    stagePill: 'Disbursed',
    votes: '18,420 votes',
    funds: '$124,500'
  },
  {
    id: 'PRB-2026-056',
    ballotId: 'BL-2026-056',
    dept: 'Civic Infrastructure',
    deptIcon: '🚌',
    title: 'Extended Night Transit Service for Shift Workers',
    city: 'Austin, USA',
    stage: 4,
    stageLabel: 'Community Voted',
    stagePill: 'Voting Closed',
    votes: '14,110 votes',
    funds: '$68,000'
  },
  {
    id: 'PRB-2026-112',
    ballotId: 'BL-2026-112',
    dept: 'Food Supply',
    deptIcon: '🌾',
    title: 'Zero-Waste Kitchens & Local Farm Network',
    city: 'London, UK',
    stage: 3,
    stageLabel: 'Ballot Sequenced',
    stagePill: 'Active Ballot',
    votes: '22,950 votes',
    funds: '$142,300'
  },
  {
    id: 'PRB-2026-073',
    ballotId: 'BL-2026-073',
    dept: 'Human Protection',
    deptIcon: '🛡️',
    title: 'Clean Water Infrastructure & Emergency Wells',
    city: 'Nairobi, Kenya',
    stage: 5,
    stageLabel: 'Facilitated & Disbursed',
    stagePill: 'Disbursed',
    votes: '31,040 votes',
    funds: '$95,000'
  },
  {
    id: 'PRB-2026-098',
    ballotId: 'BL-2026-098',
    dept: 'Environment',
    deptIcon: '🌲',
    title: 'Urban Canopy & Native Reforestation Program',
    city: 'Denver, USA',
    stage: 2,
    stageLabel: 'Truth Evidence Verified',
    stagePill: 'Verifying Truths',
    votes: '9,880 votes',
    funds: '$86,000'
  },
  {
    id: 'PRB-2026-104',
    ballotId: 'BL-2026-104',
    dept: 'Environment',
    deptIcon: '🌲',
    title: 'Coastal Watershed Protection & Filtration',
    city: 'Tokyo, Japan',
    stage: 1,
    stageLabel: 'Problem Catalogued',
    stagePill: 'Catalogued',
    votes: '5,620 votes',
    funds: '$110,000'
  }
];

/* ── Data: Fallback Certifications ─────────────────────────────── */
const MOCK_CERTIFICATIONS = [
  {
    id: 1,
    name: 'Certified Amalgamator',
    price: 'Free',
    badge: 'Core Path',
    badgeColor: '#5AAF9A',
    emoji: '🎓',
    desc: 'Become a certified Amalgamator. Master community moderation, collective consensus, and digital democracy.',
    rating: 5,
    reviews: 1420
  },
  {
    id: 2,
    name: 'Human Rights & Responsibilities',
    price: 'Free',
    badge: 'Foundational',
    badgeColor: '#2DAA6E',
    emoji: '📜',
    desc: 'Understand your natural human rights, civic duties, and the philosophical foundations of self-governance.',
    rating: 5,
    reviews: 980
  },
  {
    id: 3,
    name: 'Certified Protector',
    price: 'Free',
    badge: 'Civic Guardian',
    badgeColor: '#2E6FD8',
    emoji: '🛡️',
    desc: 'Learn dispute de-escalation, rapid emergency response coordination, and neighborhood protection frameworks.',
    rating: 5,
    reviews: 760
  },
  {
    id: 4,
    name: 'Certified Overseer',
    price: 'Free',
    badge: 'Governance & Audit',
    badgeColor: '#F5A623',
    emoji: '⚖️',
    desc: 'Verify public ledger accounting, inspect voter verification trees, and audit decentralized fund facilitations.',
    rating: 5,
    reviews: 620
  },
  {
    id: 5,
    name: 'Teachings & Certificates Creator',
    price: 'Free',
    badge: 'Educator',
    badgeColor: '#9C27B0',
    emoji: '✍️',
    desc: 'Design accredited teachings, construct objective qualification exams, and certify anyone on any skill.',
    rating: 5,
    reviews: 840
  },
  {
    id: 6,
    name: 'Universal Knowledge Base Contributor',
    price: 'Free',
    badge: 'Knowledge Hub',
    badgeColor: '#5AAF9A',
    emoji: '🌐',
    desc: 'Contribute to building an open, decentralized encyclopedia of human needs, problems, and tested solutions.',
    rating: 5,
    reviews: 510
  }
];

/* ── FAQs Data ─────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'How does posting a short video lead to a real-world solution?',
    a: 'When citizens post short videos documenting real issues in their communities, evidence is verified by peers. Verified problems enter the Problem Catalogue and are sequenced into actionable ballots for voting.'
  },
  {
    q: 'What is Mate’s role in creating ballots?',
    a: "Mate is Amalgamate's summarization tool. Its primary objective is to populate every important question in the world. We never want to duplicate the same question. Mate is here to solve the biggest problem of the world."
  },
  {
    q: 'What is a Ballot Sequence and how does it work?',
    a: 'A Ballot Sequence tracks an issue through 5 clear stages: 1) Problem Catalogued, 2) Truth Evidence Verified, 3) Ballot Formulated, 4) Community Voted, and 5) Disbursed & Facilitated through Amalgamate.'
  },
  {
    q: 'Who can create teachings and issue certifications?',
    a: 'Anyone! The Amalgamate is certified to certify you on anything. Once you prove your capability through exams and community review, you can publish teachings and certify others.'
  }
];

/* ================================================================
   1. NAVBAR COMPONENT
   ================================================================ */
function Navbar({ navigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const NAV_LINKS = [
    ['Short Videos', 'post-truths'],
    ['Create Solutions', 'create-solutions'],
    // ['Study the Data', 'study-data'],
    ['Get Certified', 'get-certified']
  ];

  return (
    <>
      <nav className={`gn ${scrolled ? 'gn--scrolled' : ''}`}>
        <button className="gn__logo" onClick={() => navigate('/')} title="AmalgaMate Home">
          <img
            className="gn__logo-img"
            src="/Assets/Donation/DonationHeader/amalgamateLogoo.svg"
            alt="Amalgamate Logo"
          />
        </button>

        <ul className="gn__links">
          {NAV_LINKS.map(([lbl, id]) => (
            <li key={lbl}>
              <button className="gn__link" onClick={() => scrollTo(id)}>
                {lbl}
              </button>
            </li>
          ))}
        </ul>

        <div className="gn__right">
          <button className="gn__ghost" onClick={() => navigate('/login')}>
            Sign In
          </button>
          <button className="gn__back-hub" onClick={() => navigate('/')}>
            ← Back to Hub
          </button>
          <button className="gn__hamburger" onClick={() => setMobileOpen(true)}>
            ☰
          </button>
        </div>
      </nav>

      {mobileOpen && <div className="gn__overlay" onClick={() => setMobileOpen(false)} />}
      <div className={`gn__drawer ${mobileOpen ? 'gn__drawer--open' : ''}`}>
        <button className="gn__drawer-close" onClick={() => setMobileOpen(false)}>
          ✕
        </button>
        <div className="gn__logo" style={{ marginBottom: 28, cursor: 'pointer' }} onClick={() => { setMobileOpen(false); navigate('/'); }}>
          <img
            className="gn__logo-img"
            src="/Assets/Donation/DonationHeader/amalgamateLogoo.svg"
            alt="Amalgamate Logo"
          />
        </div>
        {NAV_LINKS.map(([lbl, id]) => (
          <button key={lbl} className="gn__drawer-link" onClick={() => scrollTo(id)}>
            {lbl}
          </button>
        ))}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 16 }}>
          <button className="gn__back-hub" style={{ width: '100%' }} onClick={() => { setMobileOpen(false); navigate('/'); }}>
            ← Back to Hub
          </button>
          <button className="gn__ghost" style={{ width: '100%', textAlign: 'center' }} onClick={() => { setMobileOpen(false); navigate('/login'); }}>
            Sign In
          </button>
        </div>
      </div>
    </>
  );
}

/* ================================================================
   2. HERO SECTION (STAGE 1: EDUCATE)
   ================================================================ */
function Hero({ onOpenVideoModal }) {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="gh" id="educate">
      <img src="/Assets/AmalgamateHUb/educate_bg.jpg" alt="Educate hero" className="gh__bg-img" />
      <div className="gh__bg-overlay" />

      <div className="gh__content">
        <div className="gh__left">
          <span className="gh__eyebrow">🎓 STEP 01 · THE PHILOSOPHICAL FOUNDATION</span>
          <h1 className="gh__title">
            Knowledge is Power.<br />
            <span className="c-green">Let's empower the world!</span>
          </h1>

          <p className="gh__sub">
            Amalgamate's goal is to study humanity and find out what people want. Together, with that knowledge we can and will optimize our existence. The Amalgamate is certified to be able to certify you on anything. Through the process of proving knowledge and capability, anyone can become anything!
          </p>

          <div className="gh__ctas">
            <button className="gr-btn gr-btn--pink" onClick={() => scrollTo('post-truths')}>
              Start the Journey ↓
            </button>
            <button
              className="gr-btn gr-btn--outline"
              onClick={() => {
                if (getToken()) {
                  navigate('/certifications');
                } else {
                  navigate('/signin?redirect=%2Fcertifications');
                }
              }}
            >
              Explore Certifications →
            </button>
          </div>
        </div>

        {/* Right column: Interactive Video Teaser Card */}
        <div className="gh__right">
          <div className="gh-video-card" onClick={onOpenVideoModal}>
            <div className="gh-video-card__glow" />
            <div className="gh-video-card__thumb">
              <div className="gh-video-card__play">
                <span className="gh-video-card__play-triangle">▶</span>
              </div>
              <div className="gh-video-badge">
                <span className="gh-video-dot"></span>
                <span>Watch 60s Overview</span>
              </div>
              <div className="gh-video-dur">0:58</div>
            </div>
            <div className="gh-video-card__info">
              <h4>Amalgamate Educate: How It Works</h4>
              <p>Explore the 5 steps: Truths, Solutions, Data & Credentials.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   3. STAGE 2: POST TRUTHS (SHORT VIDEOS)
   ================================================================ */
function PostTruths() {
  const navigate = useNavigate();
  const [truths, setTruths] = useState([]);
  const [selectedTruth, setSelectedTruth] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTruthShorts = async () => {
      setLoading(true);
      try {
        const res = await getFeaturedShorts(4);
        const data = res?.data ?? res ?? [];
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map((v, i) => mapShortToTruth(v, i));
          setTruths(mapped);
          setSelectedTruth(mapped[0]);
        } else {
          setTruths(TRUTHS_DATA);
          setSelectedTruth(TRUTHS_DATA[0]);
        }
      } catch (err) {
        console.warn('Could not fetch real featured shorts, using defaults:', err);
        setTruths(TRUTHS_DATA);
        setSelectedTruth(TRUTHS_DATA[0]);
      } finally {
        setLoading(false);
      }
    };
    fetchTruthShorts();
  }, []);

  const handleTogglePlay = (e, item) => {
    e.stopPropagation();
    setSelectedTruth(item);
    if (playingId === item.id) {
      setPlayingId(null);
    } else {
      setPlayingId(item.id);
    }
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(prev => !prev);
  };

  const displayList = truths.length > 0 ? truths : TRUTHS_DATA;

  return (
    <section className="gpt-sec adv-sec" id="post-truths">
      <div className="adv-sec__hdr">
        <span className="sec-step-badge">STEP 02 · SHORT VIDEOS</span>
        <h2 className="adv-sec__title">
          Post <span className="c-green">Short Videos</span>
        </h2>
        <p className="adv-sec__sub">
          Citizens record ground-level observations and community challenges, linking real video evidence directly to actionable ballots for collective decision-making.
        </p>
      </div>

      <div className="gpt-grid">
        {displayList.map((item) => {
          const isPlaying = playingId === item.id;
          return (
            <div
              key={item.id}
              className={`gpt-card ${selectedTruth?.id === item.id ? 'gpt-card--active' : ''}`}
              onClick={(e) => handleTogglePlay(e, item)}
            >
              {/* Visual Container (Takes ~70% space) */}
              <div className="gpt-card__visual">
                {isPlaying && item.videoUrl ? (
                  <video
                    key={`playing-${item.id}`}
                    src={item.videoUrl}
                    autoPlay
                    loop
                    playsInline
                    muted={isMuted}
                    className="gpt-card__media-video"
                  />
                ) : item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt={item.author} className="gpt-card__media-img" />
                ) : item.videoUrl ? (
                  <video
                    src={`${item.videoUrl}#t=0.5`}
                    preload="metadata"
                    muted
                    playsInline
                    className="gpt-card__media-video"
                  />
                ) : (
                  <div className="gpt-card__media-placeholder">
                    <span style={{ fontSize: 32 }}>🎥</span>
                  </div>
                )}

                {/* Floating Top Category, Duration & Mute Toggle */}
                <div className="gpt-card__floating-top">
                  {item.category && (
                    <span className="gpt-card__category" style={{ borderColor: item.badgeColor, color: item.badgeColor }}>
                      {item.categoryIcon} {item.category}
                    </span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isPlaying && (
                      <button
                        className="gpt-card__sound-btn"
                        onClick={handleToggleMute}
                        title={isMuted ? "Unmute audio" : "Mute audio"}
                      >
                        {isMuted ? "🔇" : "🔊"}
                      </button>
                    )}
                    {item.duration && (
                      <span className="gpt-card__duration">{item.duration}</span>
                    )}
                  </div>
                </div>

                {/* Play / Pause Button Overlay */}
                <div
                  className={`gpt-card__play-icon ${isPlaying ? 'gpt-card__play-icon--playing' : ''}`}
                  onClick={(e) => handleTogglePlay(e, item)}
                >
                  {isPlaying ? "⏸" : "▶"}
                </div>

                {/* Bottom Shadow Gradient Scrim */}
                <div className="gpt-card__scrim" />

                {/* Bottom Metrics Overlay */}
                <div className="gpt-card__overlay-tag">
                  <span className="gpt-card__stat-pill">❤️ {item.likes ?? 0}</span>
                  <span className="gpt-card__stat-pill">💬 {item.comments ?? 0}</span>
                </div>
              </div>

              {/* Bottom Content Body (Takes ~30% space) */}
              <div className="gpt-card__body">
                <div className="gpt-card__author-row">
                  {item.avatarUrl ? (
                    <img src={item.avatarUrl} alt={item.author} className="gpt-card__avatar-img" />
                  ) : (
                    <div className="gpt-card__avatar">{item.avatar}</div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span className="gpt-card__author">{item.author}</span>
                    {item.city && <span className="gpt-card__city">📍 {item.city}</span>}
                  </div>
                </div>

                {item.title && <h3 className="gpt-card__title">{item.title}</h3>}
                {item.previewDesc && <p className="gpt-card__desc">{item.previewDesc}</p>}

                {item.linkedBallot && (
                  <div className="gpt-card__footer">
                    <span className="gpt-linked-tag">
                      Linked Ballot: <strong>{item.linkedBallot}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="gpt-actions">
        <button className="gr-btn gr-btn--pink" onClick={() => navigate('/shorts-videos')}>
          Watch Shorts Feed
        </button>
        <button className="gr-btn gr-btn--outline" onClick={() => navigate('/upload-shorts')}>
          Upload Short
        </button>
      </div>
    </section>
  );
}

/* ================================================================
   4. STAGE 3: CREATE SOLUTIONS (MATE ADD BALLOT)
   ================================================================ */
function CreateSolutions() {
  const navigate = useNavigate();

  return (
    <section className="gcs-sec adv-sec" id="create-solutions">
      <div className="adv-sec__hdr">
        <span className="sec-step-badge">STEP 03 · DELIBERATE & PROPOSE</span>
        <h2 className="adv-sec__title">
          Create Solutions with <span className="c-green">Mate</span>
        </h2>
        <p className="adv-sec__sub">
          Don't just point out problems—solve them. Collaborate with Mate to formulate clear, unbiased ballots and structure actionable questions for the community to vote on.
        </p>
      </div>

      <div className="gcs-container">
        {/* Left: Authentic Mate AI Chat Simulation */}
        <div className="gcs-chat glass-card">
          <div className="gcs-chat__hdr">
            <div className="gcs-chat__mate-avatar">
              <span>💬</span>
              <span className="gcs-online-dot"></span>
            </div>
            <div>
              <strong>Mate AI</strong>
              <span>Civic Co-Pilot & Ballot Formulator</span>
            </div>
            <span className="gcs-badge-live">● Online</span>
          </div>

          <div className="gcs-chat__body">
            {/* User Message */}
            <div className="gcs-msg gcs-msg--user">
              <span className="gcs-msg__lbl">Citizen @nurse_sarah</span>
              <div className="gcs-bubble gcs-bubble--user">
                Healthcare workers in District 3 have no safe public transit after 10 PM. We have recorded short videos from the night shift. How can we get this resolved?
              </div>
            </div>

            {/* Mate AI Response */}
            <div className="gcs-msg gcs-msg--mate">
              <span className="gcs-msg__lbl">Mate AI</span>
              <div className="gcs-bubble gcs-bubble--mate">
                I reviewed your short video evidence and transit schedules. I've formulated an actionable, deduplicated <strong>Ballot Proposal</strong> ready for community voting:
              </div>
            </div>

            {/* Generated Ballot Proposal Card (Real Amalgamate Style) */}
            <div className="gcs-ballot-card">
              <div className="gcs-ballot-card__top">
                <span className="gcs-ballot-num">BALLOT #BL-2026-056</span>
                <span className="gcs-ballot-status">MAIN BALLOT</span>
              </div>
              <h4>Extend Route 14 Night Transit Service for Hospital Staff</h4>
              <p>Add two dedicated low-emission buses between General Hospital and Downtown from 10:30 PM to 2:00 AM.</p>

              <div className="gcs-ballot-options-list">
                <div className="gcs-ballot-option">
                  <span className="gcs-option-bullet">A</span>
                  <span>Yes — Allocate dedicated night transit service</span>
                </div>
                <div className="gcs-ballot-option">
                  <span className="gcs-option-bullet">B</span>
                  <span>No — Maintain existing daytime schedule</span>
                </div>
                <div className="gcs-ballot-option">
                  <span className="gcs-option-bullet">C</span>
                  <span>Subsidize late-night rideshare vouchers</span>
                </div>
              </div>

              <div className="gcs-ballot-card__actions">
                <button className="gcs-keep-ballot-btn" onClick={() => navigate('/add-ballot')}>
                  Keep Ballot & Add to Vote →
                </button>
              </div>
            </div>

            <div className="gcs-msg gcs-msg--mate">
              <div className="gcs-bubble gcs-bubble--mate">
                This ballot is ready to publish. Once live, citizens can vote and participate in collective decision-making.
              </div>
            </div>
          </div>

          {/* Interactive Input Bar */}
          <div className="gcs-chat__input-row">
            <input
              type="text"
              readOnly
              value="Describe a community issue or question for Mate AI..."
              className="gcs-mock-input"
              onClick={() => navigate('/add-ballot')}
            />
            <button className="gr-btn gr-btn--pink" onClick={() => navigate('/add-ballot')}>
              Create Ballot
            </button>
          </div>
        </div>

        {/* Right: 3 Trust-Building Core Pillars */}
        <div className="gcs-features">
          <div className="gcs-feat-card">
            <span className="gcs-feat-icon">🌐</span>
            <div>
              <h4>Zero Duplication & Global Summarization</h4>
              <p>Mate indexes every community question worldwide, automatically deduplicating and merging related concerns into unified, high-impact ballots.</p>
            </div>
          </div>

          <div className="gcs-feat-card">
            <span className="gcs-feat-icon">⚖️</span>
            <div>
              <h4>Neutral & Balanced Formulation</h4>
              <p>Every ballot is formulated without partisan slant or emotional bias, ensuring clear, objective phrasing so voters can make informed decisions.</p>
            </div>
          </div>

          <div className="gcs-feat-card">
            <span className="gcs-feat-icon">📹</span>
            <div>
              <h4>Ground Evidence to Collective Action</h4>
              <p>Directly link citizen short videos and verified observations to ballots—giving every vote visual proof and real community accountability.</p>
            </div>
          </div>

          <div className="gcs-actions-box">
            <button className="gr-btn gr-btn--pink" onClick={() => navigate('/add-ballot')}>
              Launch Mate & Create Ballot
            </button>
            <button className="gr-btn gr-btn--outline" onClick={() => navigate('/ballots')}>
              Explore Community Ballots
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   5. STAGE 4: STUDY THE DATA (PROBLEM CATALOGUE & BALLOT SEQUENCES)
   ================================================================ */
function StudyData() {
  const [selectedDept, setSelectedDept] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const DEPARTMENTS = ['All', 'Healthcare', 'Environment', 'Human Protection', 'Food Supply', 'Civic Infrastructure'];

  const filteredItems = CATALOGUE_DATA.filter((item) => {
    if (selectedDept !== 'All' && item.dept !== selectedDept) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(q) ||
        item.city.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <section className="gsd-sec adv-sec" id="study-data">
      <div className="adv-sec__hdr">
        <span className="sec-step-badge">📊 STEP 04 · VERIFIABLE INSIGHTS</span>
        <h2 className="adv-sec__title">
          Study the Data: <span className="c-green">Problem Catalogue & Sequences</span>
        </h2>
        <p className="adv-sec__sub">
          Explore public data on what humanity needs. Track community issues from initial problem cataloguing through sequential ballot votes to funded facilitation.
        </p>
      </div>

      {/* Live Metrics Counter */}
      <div className="gsd-metrics-grid">
        <div className="gsd-metric-card">
          <span className="gsd-metric-icon">📑</span>
          <span className="gsd-metric-val">1,420+</span>
          <span className="gsd-metric-lbl">Catalogued Problems</span>
          <span className="gsd-metric-sub">Documented by citizens worldwide</span>
        </div>
        <div className="gsd-metric-card">
          <span className="gsd-metric-icon">🔄</span>
          <span className="gsd-metric-val">856</span>
          <span className="gsd-metric-lbl">Ballot Sequences</span>
          <span className="gsd-metric-sub">Multi-stage progression pipelines</span>
        </div>
        <div className="gsd-metric-card">
          <span className="gsd-metric-icon">🗳️</span>
          <span className="gsd-metric-val">240,000+</span>
          <span className="gsd-metric-lbl">Total Votes Cast</span>
          <span className="gsd-metric-sub">Verified cryptographic ballots</span>
        </div>
        <div className="gsd-metric-card">
          <span className="gsd-metric-icon">🌱</span>
          <span className="gsd-metric-val">312</span>
          <span className="gsd-metric-lbl">Facilitated Solutions</span>
          <span className="gsd-metric-sub">Real-world outcomes funded</span>
        </div>
      </div>

      {/* Sequence Progression Banner */}
      <div className="gsd-sequence-tracker glass-card">
        <div className="gsd-tracker-hdr">
          <strong>The Amalgamate 5-Stage Sequence:</strong>
          <span>How an observed problem turns into a funded real-world solution</span>
        </div>
        <div className="gsd-steps-line">
          <div className="gsd-step active">
            <span className="gsd-step-num">1</span>
            <span>Problem Catalogued</span>
          </div>
          <span className="gsd-arrow">→</span>
          <div className="gsd-step active">
            <span className="gsd-step-num">2</span>
            <span>Truth Videos Verified</span>
          </div>
          <span className="gsd-arrow">→</span>
          <div className="gsd-step active">
            <span className="gsd-step-num">3</span>
            <span>Ballot Sequenced</span>
          </div>
          <span className="gsd-arrow">→</span>
          <div className="gsd-step active">
            <span className="gsd-step-num">4</span>
            <span>Community Voted</span>
          </div>
          <span className="gsd-arrow">→</span>
          <div className="gsd-step active">
            <span className="gsd-step-num">5</span>
            <span>Disbursed & Facilitated</span>
          </div>
        </div>
      </div>

      {/* Filterable Problem Catalogue Dashboard */}
      <div className="gsd-dashboard glass-card">
        <div className="gsd-dash-hdr">
          <div className="gsd-dept-tabs">
            {DEPARTMENTS.map((dept) => (
              <button
                key={dept}
                className={`gsd-tab ${selectedDept === dept ? 'gsd-tab--active' : ''}`}
                onClick={() => setSelectedDept(dept)}
              >
                {dept}
              </button>
            ))}
          </div>

          <div className="gsd-search">
            <input
              type="text"
              placeholder="Search problem, city, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="gsd-search-input"
            />
          </div>
        </div>

        <div className="gsd-table-wrapper">
          <table className="gsd-table">
            <thead>
              <tr>
                <th>ID / Department</th>
                <th>Problem & Initiative</th>
                <th>Location</th>
                <th>Sequence Stage</th>
                <th>Votes / Backing</th>
                <th>Facilitation</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="gsd-dept-col">
                      <span className="gsd-dept-icon">{item.deptIcon}</span>
                      <div>
                        <strong>{item.id}</strong>
                        <span>{item.dept}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="gsd-title-col">
                      <strong>{item.title}</strong>
                      <span className="gsd-ballot-link">Linked: {item.ballotId}</span>
                    </div>
                  </td>
                  <td>
                    <span className="gsd-city-tag">📍 {item.city}</span>
                  </td>
                  <td>
                    <div className="gsd-stage-col">
                      <span className={`gsd-stage-badge stage--${item.stage}`}>{item.stagePill}</span>
                      <small>Stage {item.stage} of 5</small>
                    </div>
                  </td>
                  <td>
                    <strong>{item.votes}</strong>
                  </td>
                  <td>
                    <span className="gsd-fund-tag">{item.funds}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   6. STAGE 5: GET CERTIFIED (DECENTRALIZED ACCREDITATION)
   ================================================================ */
function GetCertified({ certifications, isLoading }) {
  const navigate = useNavigate();

  const handleActionClick = () => {
    if (getToken()) {
      navigate('/certifications');
    } else {
      navigate('/signin?redirect=%2Fcertifications');
    }
  };

  // Sort by content richness (modules + questions) descending, then newest, and pick top 3
  const hasApiCerts = certifications && certifications.length > 0;
  const displayCerts = useMemo(() => {
    if (!hasApiCerts) {
      return MOCK_CERTIFICATIONS.slice(0, 3);
    }

    const sorted = [...certifications].sort((a, b) => {
      const aRichness = (Number(a.totalModules) || 0) * 2 + (Number(a.totalQuestions) || 0);
      const bRichness = (Number(b.totalModules) || 0) * 2 + (Number(b.totalQuestions) || 0);
      if (bRichness !== aRichness) {
        return bRichness - aRichness;
      }
      return new Date(b.createdOn || 0) - new Date(a.createdOn || 0);
    });

    return sorted.slice(0, 3).map((c, idx) => ({
      id: c.certificationId || c.id || idx,
      name: c.name || c.certificationName || c.title || 'Certification Program',
      desc: c.description || 'Verified qualification module on Amalgamate.',
      price: c.price && Number(c.price) > 0 ? `$${c.price}` : 'Free',
      badge: c.badgeType || c.level || (idx === 0 ? 'Featured' : idx === 1 ? 'Popular' : 'Accredited'),
      badgeColor: idx % 3 === 0 ? '#5AAF9A' : idx % 3 === 1 ? '#2DAA6E' : '#2E6FD8',
      rating: 5,
      reviews: 320 + idx * 140,
      totalModules: c.totalModules || 0,
      totalQuestions: c.totalQuestions || 0,
      emoji: idx % 4 === 0 ? '🎓' : idx % 4 === 1 ? '📜' : idx % 4 === 2 ? '🛡️' : '⚖️'
    }));
  }, [certifications, hasApiCerts]);

  return (
    <section className="gpr adv-sec" id="get-certified">
      <div className="adv-sec__hdr">
        <span className="sec-step-badge">🎓 STEP 05 · PROVE CAPABILITY</span>
        <h2 className="adv-sec__title">
          Get Certified. <span className="c-green">Anyone Can Become Anything.</span>
        </h2>
        <p className="adv-sec__sub">
          Accreditation by proof of competence. Complete interactive modules, pass cryptographic exams, and earn permanent on-profile credentials.
        </p>
      </div>

      {isLoading ? (
        <div className="adv-loading-container" style={{ textAlign: 'center', padding: '40px 0' }}>
          <div className="adv-spinner"></div>
          <p style={{ marginTop: '12px', color: 'rgba(255,255,255,0.6)' }}>Loading certifications...</p>
        </div>
      ) : (
        <>
          <div className="gpr__grid">
            {displayCerts.map((prod) => (
              <div key={prod.id} className="gpr__card">
                {prod.badge && (
                  <span className="gpr__badge" style={{ background: prod.badgeColor }}>
                    {prod.badge}
                  </span>
                )}
                <div className="gpr__img-wrap">
                  <span className="gpr__emoji">{prod.emoji}</span>
                  <div className="gpr__img-glow" />
                </div>
                <div className="gpr__body">
                  <h3 className="gpr__name">{prod.name}</h3>
                  <p className="gpr__desc">{prod.desc}</p>
                  <div className="gpr__rating">
                    <span className="gpr__stars">{'★'.repeat(prod.rating)}</span>
                    <span className="gpr__rcount">({prod.reviews} certified)</span>
                  </div>
                  <div className="gpr__price-row" style={{ marginTop: '16px' }}>
                    <span className="gpr__price">{prod.price}</span>
                  </div>
                  <button className="gr-btn gr-btn--pink gpr__add" onClick={handleActionClick}>
                    Enroll & Certify →
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="gpr__view-all" style={{ textAlign: 'center', marginTop: '48px' }}>
            <button
              className="gr-btn gr-btn--pink"
              style={{ padding: '14px 40px', fontSize: '14px', borderRadius: '30px' }}
              onClick={handleActionClick}
            >
              Explore All Certifications & Exams
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/* ================================================================
   7. FAQ SECTION
   ================================================================ */
function FAQ() {
  const [open, setOpen] = useState(null);

  return (
    <section className="gfq adv-sec" id="faq">
      <div className="adv-sec__hdr">
        <h2 className="adv-sec__title">
          Frequently Asked <span className="c-green">Questions</span>
        </h2>
      </div>
      <div className="gfq__list">
        {FAQS.map((f, i) => (
          <div key={i} className={`gfq__item ${open === i ? 'gfq__item--open' : ''}`}>
            <button className="gfq__q" onClick={() => setOpen(open === i ? null : i)}>
              <span>{f.q}</span>
              <span className="gfq__chevron">{open === i ? '▲' : '▼'}</span>
            </button>
            <div className="gfq__a">
              <p>{f.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================================================================
   8. VIDEO LIGHTBOX MODAL
   ================================================================ */
function VideoModal({ isOpen, onClose, activeVideo }) {
  const navigate = useNavigate();
  if (!isOpen) return null;

  return (
    <div className="gh-modal-overlay" onClick={onClose}>
      <div className="gh-modal-container glass-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '580px', width: '92%' }}>
        <button className="gh-modal-close" onClick={onClose}>
          ✕
        </button>
        <div className="gh-modal-video-box">
          {activeVideo && activeVideo.videoUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' }}>
              <video
                controls
                autoPlay
                playsInline
                src={activeVideo.videoUrl}
                style={{
                  width: '100%',
                  maxHeight: '65vh',
                  borderRadius: '14px',
                  backgroundColor: '#000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)'
                }}
              />
              <div style={{ textAlign: 'left', width: '100%', padding: '0 8px' }}>
                <h3 style={{ color: '#fff', fontSize: '18px', margin: '4px 0 8px' }}>{activeVideo.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5 }}>{activeVideo.previewDesc}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
                  <span style={{ fontSize: '13px', color: '#5AAF9A' }}>
                    {activeVideo.author} · {activeVideo.category}
                  </span>
                  <button
                    className="gr-btn gr-btn--pink"
                    style={{ padding: '8px 16px', fontSize: '12px' }}
                    onClick={() => {
                      onClose();
                      navigate('/shorts-videos');
                    }}
                  >
                    Open in Shorts Feed ➔
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="gh-modal-placeholder">
              <div className="gh-modal-play-icon">🎬</div>
              <h3>Amalgamate Educate: 60-Second Overview</h3>
              <p>
                "Knowledge is power. Amalgamate was created to study humanity, post ground-level truths, create actionable solutions with Mate, study public data, and certify capability."
              </p>
              <div className="gh-modal-steps-preview">
                <span>1. Educate</span>
                <span>2. Post Truths</span>
                <span>3. Create Solutions</span>
                <span>4. Study Data</span>
                <span>5. Get Certified</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   9. PAGE ROOT
   ================================================================ */
export default function GearPage() {
  const navigate = useNavigate();
  const [certs, setCerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchCerts = async () => {
      setIsLoading(true);
      try {
        const res = await getAllCertifications();
        const list = res?.data ?? res ?? [];
        setCerts(list);
      } catch (error) {
        console.error('Failed to fetch certifications in GearPage', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCerts();
  }, []);

  const handleOpenTeaser = () => {
    setActiveVideo(null);
    setVideoModalOpen(true);
  };

  const handlePlayTruthVideo = (video) => {
    setActiveVideo(video);
    setVideoModalOpen(true);
  };

  return (
    <>
      <SEO
        title="Educate - Knowledge, Truths & Solutions | Amalgamate"
        description="Discover truth, create solutions with Mate AI, study public problem data and ballot sequences, and earn verified certifications on Amalgamate."
        image="/Assets/AmalgamateHUb/educate_bg.jpg"
        slugPath="/educate"
      />
      <div className="gear-root">
        <Navbar navigate={navigate} />
        <Hero onOpenVideoModal={handleOpenTeaser} />
        <PostTruths />
        <CreateSolutions />
        {/* <StudyData /> */}
        <GetCertified certifications={certs} isLoading={isLoading} />
        <FAQ />
        <Footer />
        <VideoModal
          isOpen={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          activeVideo={activeVideo}
        />
      </div>
    </>
  );
}
